/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useCallback, useContext, useRef, useState,
} from 'react';
import InvitationsView from 'screens/Admin/Invitations/components/View';
import useFetchTableData from 'hooks/useFetchTableData';
import AdminInvitationsServices from 'services/api/AdminInvitations';
import AdminContainer from 'components/Admin/AdminContainer';
import { useLocation, useParams } from 'react-router-dom';
import { LoadingContext } from 'context/Loading';
import CreateInvitationModal from 'components/Modal/CreateInvitationModal';
import EditInvitationModal from 'components/Modal/EditInvitationModal';
import handlePromise from 'utils/Promise';
import { b64toBlob } from 'utils/downloadBase64';
import { downloadFileFromBase64 } from 'utils/downloadFileFromBase64';

import { ModalErrorContext } from 'context/ModalError';

function Invitations() {
  const { id, type } = useParams();
  const location = useLocation();
  const { toggleLoading } = useContext( LoadingContext );
  const { openErrorModal } = useContext( ModalErrorContext );

  const [invitationToClone, setInvitationToClone] = useState<any>( null );
  const [dataReloaded, setDataReloaded] = useState( new Date().valueOf() );
  const { fetchData, data, dataLoading } = useFetchTableData( {
    promise: ( params ) => AdminInvitationsServices.getAdminInvitations( params ),
    deps: [dataReloaded],
  } );

  const [showCreateInvitationModal, setCreateInvitationModal] = useState( false );
  const [showEditInvitationModal, setShowEditInvitationModal] = useState( false );

  const dataInvitationRef = useRef<any>();

  const openCreateInvitationModal = useCallback( ( ) => {
    setCreateInvitationModal( ( state ) => !state );
  }, [] );

  const openEditInvitationModal = useCallback( async ( dataInvitation: any ) => {
    toggleLoading( true );
    const [error, , invitationData] = await handlePromise(
      AdminInvitationsServices.getAdminInvitationsById( dataInvitation.id ),
    );
    if ( error ) {
      openErrorModal( error );
    } else {
      dataInvitationRef.current = invitationData;
      setShowEditInvitationModal( true );
    }
    toggleLoading( false );
  }, [] );

  const openCloneInvitationModal = useCallback( async ( dataInvitation: any ) => {
    toggleLoading( true );
    const [error, , invitationData] = await handlePromise(
      AdminInvitationsServices.getAdminInvitationsById( dataInvitation.id ),
    );

    if ( error ) {
      openErrorModal( error );
    } else {
      setInvitationToClone( invitationData );
      setCreateInvitationModal( true );
    }
    toggleLoading( false );
  }, [] );

  const downloadDocument = useCallback( async (
    invitacionUid: string,
    documentoUid: string,
    nombreArchivo: string,
  ) => {
    if ( !nombreArchivo ) {
      openErrorModal( 'El nombre del archivo es obligatorio para la descarga.' );
      return;
    }
    toggleLoading( true );
    const [error, response, downloadDocumentData] = await handlePromise(
      AdminInvitationsServices.getDownloadDocumentAsAdmin( invitacionUid, documentoUid ),
    );
    toggleLoading( false );
    if ( error ) {
      const detail = ( response as any )?.data?.detail;
      openErrorModal( detail || error );
      return;
    }
    if ( !response?.ok || !downloadDocumentData ) {
      const detail = ( response as any )?.data?.detail;
      openErrorModal(
        detail || 'No se pudo descargar el documento. Intente nuevamente más tarde.',
      );
      return;
    }
    if ( !downloadDocumentData.includes( 'base64,' ) ) {
      openErrorModal( 'No se pudo obtener el documento. Intente nuevamente más tarde.' );
      return;
    }
    try {
      downloadFileFromBase64( downloadDocumentData, nombreArchivo );
    } catch ( err ) {
      openErrorModal(
        err instanceof Error ? err.message : 'No se pudo descargar el documento.',
      );
    }
  }, [] );

  const getDocumentNda = useCallback( async ( ) => {
    toggleLoading( true );
    const [error, response, downloadDocumentData] = await handlePromise(
      AdminInvitationsServices.getAdminGetDocumentNdaById( dataInvitationRef.current?.id ),
    );
    if ( error ) {
      openErrorModal( error );
    } else {
      if ( !response.ok || !downloadDocumentData || !downloadDocumentData.includes( 'base64,' ) ) {
        openErrorModal( 'No se pudo obtener el documento. Intente nuevamente más tarde.' );
        return;
      }

      const posBase = downloadDocumentData.indexOf( 'base64,' );
      const typeDoc = downloadDocumentData.substring( 5, posBase - 1 );
      const dataResult = downloadDocumentData.replace( 'data:application/pdf;base64,', '' );

      if ( !dataResult || dataResult.length < 1000 ) {
        openErrorModal( 'El documento está dañado o vacío. No se puede visualizar.' );
        return;
      }

      const blob = b64toBlob( dataResult, typeDoc );
      const blobUrl = URL.createObjectURL( blob );

      const link = document.createElement( 'a' );
      link.href = blobUrl;
      link.download = `documento-NDA-${dataInvitationRef.current?.invitacionUid}.pdf`;
      document.body.appendChild( link );
      link.click();
      document.body.removeChild( link );
      URL.revokeObjectURL( blobUrl );
    }
    toggleLoading( false );
  }, [] );

  const closeCreateInvitationModal = useCallback( ( ) => {
    setCreateInvitationModal( false );
    setInvitationToClone( null );
  }, [] );

  const closeEditInvitationModal = useCallback( ( ) => {
    setShowEditInvitationModal( false );
    dataInvitationRef.current = null;
  }, [] );

  return (
    <AdminContainer
      headerTitle="dddwdw"
      type={type}
      id={id ? Number( id ) : undefined}
    >
      <InvitationsView
        openCreateInvitationModal={openCreateInvitationModal}
        openCloneInvitationModal={openCloneInvitationModal}
        openEditInvitationModal={openEditInvitationModal}
        invitations={data}
        fetchData={fetchData}
        isLoading={dataLoading}
        reloadData={() => setDataReloaded( new Date().valueOf() )}
      />

      {showCreateInvitationModal && (
      <CreateInvitationModal
        key={invitationToClone?.id || 'create-new'}
        show
        initialData={invitationToClone}
        onClose={closeCreateInvitationModal}
        reloadData={() => setDataReloaded( new Date().valueOf() )}
      />
      )}

      {showEditInvitationModal && (
      <EditInvitationModal
        dataInvitation={dataInvitationRef.current}
        show
        onClose={closeEditInvitationModal}
        downloadNda={getDocumentNda}
        downloadDocument={downloadDocument}
        reloadData={() => setDataReloaded( new Date().valueOf() )}
      />
      )}

    </AdminContainer>
  );
}

export default Invitations;
